import { useMemo } from "react"

import { MultiSelect, Select } from "~/ui-rtk/components/ui/controls"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"

import {
  FAMILY_OPTIONS,
  LTV_COHORT_CHANNELS,
  LTV_COHORT_VALUE_BANDS,
  SKU_PREFIX_LEVELS,
  type CohortFamily,
} from "../constants"
import type { LtvCohortFilters } from "../connect"

const CHANNEL_OPTIONS: TMultiSelectOption[] = LTV_COHORT_CHANNELS.map(
  channel => ({ value: channel, label: channel }),
)

const VALUE_BAND_OPTIONS: TMultiSelectOption[] = LTV_COHORT_VALUE_BANDS.map(
  band => ({ value: band, label: band }),
)

const toSelected = (
  values: string[],
  options: TMultiSelectOption[],
): TMultiSelectOption[] =>
  options.filter(option => values.includes(option.value))

export type CohortFiltersProps = {
  filters: LtvCohortFilters
  onChange: (filters: LtvCohortFilters) => void
  productOptions: { options: TMultiSelectOption[]; isLoading: boolean }
  collectionOptions: { options: TMultiSelectOption[]; isLoading: boolean }
  skuPrefixOptions: { options: TMultiSelectOption[]; isLoading: boolean }
}

/**
 * Selected values may fall outside the top-N option list for the current
 * window (or the list may be mid-refetch); appending them keeps every active
 * filter visible and removable.
 */
const withSelectedValues = (
  options: TMultiSelectOption[],
  selected: string[],
): TMultiSelectOption[] => {
  const known = new Set(options.map(option => option.value))
  return [
    ...options,
    ...selected
      .filter(value => !known.has(value))
      .map(value => ({ value, label: value })),
  ]
}

export default function CohortFilters({
  filters,
  onChange,
  productOptions,
  collectionOptions,
  skuPrefixOptions,
}: CohortFiltersProps) {
  const productOptionsWithSelected = useMemo(
    () => withSelectedValues(productOptions.options, filters.productIds),
    [productOptions.options, filters.productIds],
  )

  const collectionOptionsWithSelected = useMemo(
    () => withSelectedValues(collectionOptions.options, filters.collectionIds),
    [collectionOptions.options, filters.collectionIds],
  )

  const skuOptionsWithSelected = useMemo(
    () => withSelectedValues(skuPrefixOptions.options, filters.skuPrefixes),
    [skuPrefixOptions.options, filters.skuPrefixes],
  )

  return (
    <div className="flex flex-wrap items-end gap-3">
      <MultiSelect
        label="First order channel"
        options={CHANNEL_OPTIONS}
        value={toSelected(filters.channels, CHANNEL_OPTIONS)}
        onChange={selected =>
          onChange({ ...filters, channels: selected.map(o => o.value) })
        }
        className="min-w-52"
      />
      <MultiSelect
        label="First order value"
        options={VALUE_BAND_OPTIONS}
        value={toSelected(filters.valueBands, VALUE_BAND_OPTIONS)}
        onChange={selected =>
          onChange({ ...filters, valueBands: selected.map(o => o.value) })
        }
        className="min-w-44"
      />
      <div className="min-w-52">
        <Select
          value={filters.family}
          options={FAMILY_OPTIONS.map(option => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={event =>
            onChange({
              ...filters,
              family: event.target.value as CohortFamily,
            })
          }
        />
      </div>
      {filters.family === "product" && (
        <MultiSelect
          label={productOptions.isLoading ? "Products (loading…)" : "Products"}
          options={productOptionsWithSelected}
          value={toSelected(filters.productIds, productOptionsWithSelected)}
          onChange={selected =>
            onChange({ ...filters, productIds: selected.map(o => o.value) })
          }
          className="min-w-64"
        />
      )}
      {filters.family === "collection" && (
        <MultiSelect
          label={
            collectionOptions.isLoading
              ? "Collections (loading…)"
              : "Collections"
          }
          options={collectionOptionsWithSelected}
          value={toSelected(
            filters.collectionIds,
            collectionOptionsWithSelected,
          )}
          onChange={selected =>
            onChange({
              ...filters,
              collectionIds: selected.map(o => o.value),
            })
          }
          className="min-w-64"
        />
      )}
      {filters.family === "sku" && (
        <>
          <div className="w-36">
            <Select
              value={String(filters.skuPrefixLength)}
              options={SKU_PREFIX_LEVELS.map(level => ({
                value: String(level),
                label: `First ${level} char${level === 1 ? "" : "s"}`,
              }))}
              onChange={event =>
                onChange({
                  ...filters,
                  skuPrefixLength: Number(event.target.value),
                  // Prefixes are level-specific; a level switch resets them.
                  skuPrefixes: [],
                })
              }
            />
          </div>
          <MultiSelect
            label={
              skuPrefixOptions.isLoading
                ? "SKU prefixes (loading…)"
                : "SKU prefixes"
            }
            options={skuOptionsWithSelected}
            value={toSelected(filters.skuPrefixes, skuOptionsWithSelected)}
            onChange={selected =>
              onChange({
                ...filters,
                skuPrefixes: selected.map(o => o.value),
              })
            }
            className="min-w-52"
          />
        </>
      )}
    </div>
  )
}
